import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy, ViewChild, viewChild } from '@angular/core';
import { AuthModalComponent } from '@app/core/components/auth-modal/auth-modal.component';
import { NotFoundError } from '@app/lingo2-errors';
import { NotificationsService } from '@app/notifications/notifications.service';
import { loadMe } from '@app/store/actions/profile.actions';
import { ContextService } from '@core/services/context.service';
import { PlatformService } from '@core/services/platform.service';
import { environment } from '@env/environment';
import { DestroyableComponent } from '@models/destroyable.component';
import { Store } from '@ngrx/store';
import { LogInEndpoint, PwdRestoreEndpoint, SignUpEndpoint } from 'lingo2-api-models';
import { IAuthTokensResponse, ISocialSignupRequest, SocialNetworkEnum, User, UserStatusEnum } from 'lingo2-models';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, Observable, of, ReplaySubject, Subscription, throwError, timer } from 'rxjs';
import { catchError, filter, first, map, retry, takeUntil, tap } from 'rxjs/operators';
import { AccessTokenService } from '../access-token.service';
import { ModalService } from '../modal.services';

/*
 */
export interface IAccountChangeResult {
  id: string;
  status: 'changed' | 'code_required' | 'error' | 'code_failed';
  model?: 'password' | 'new_password';
  error?: string;
}
export interface AuthModalOptions {
  caller: string;
  reason: string;
  view?: AuthModalModeType;
}

const ACCESS_KEY = environment.access_token_key;
export type AuthUserStateType = 'guest' | 'signedIn' | 'signedUp' | 'signedOut';
export type AuthModalModeType =
  | 'social'
  | 'signin'
  | 'signup'
  | 'restore'
  | 'restored'
  | 'smsFlow'
  | 'emailRestoredFlow'
  | 'signout';

/*
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService extends DestroyableComponent implements OnDestroy {
  public stateSubject = this.register(new ReplaySubject<AuthUserStateType>(1));
  private userSubject = this.register(new ReplaySubject<User>(1));
  private _user: User;
  private inited = false;
  private fnAuthSuccess: (user: User) => void;
  private getUserById$$: Subscription;

  /* Modal
   */
  private modalVisible = false;
  // private modalVisibleSubject = this.register(new BehaviorSubject<boolean>(this.modalVisible));
  // public modalVisible$ = this.modalVisibleSubject.asObservable();
  // private modalMode: AuthModalModeType = 'social';
  // private modalModeSubject = this.register(new BehaviorSubject<AuthModalModeType>(this.modalMode));
  // public modalMode$ = this.modalModeSubject.asObservable();
  // private modalOptionsSubject = this.register(new BehaviorSubject<AuthModalOptions>(null));
  // public modalOptions$ = this.modalOptionsSubject.asObservable();

  /* Todo. Other. Review.
   */
  protected online_status_interval = 5 * 60 * 1000;
  protected back_url_key = 'APP_AUTH_BACK_URL';
  protected _backUrl: string;
  private sendingOnlineStatus = false;

  /*
   */
  constructor(
    private http: HttpClient,
    private token_srv: AccessTokenService,
    private modal_srv: ModalService,
    private cookie_srv: CookieService,
    private contextService: ContextService,
    private readonly store: Store,
    private notificationsService: NotificationsService,
    protected readonly platform: PlatformService,
  ) {
    super(platform);
  }

  /*
   */
  public getUserGeo = (): string => this.cookie_srv.get('User-Geo');
  public getUserCurrency = (): string => this.cookie_srv.get('User-Currency');

  /*
   */
  private getDefReqParams = () => ({ domain: environment.cookie_domain, key: ACCESS_KEY });
  private getCaptchaHeaders = (token: string) => ({
    headers: new HttpHeaders({ ['X-Captcha-Token']: token || '' }),
  });

  public get user$(): Observable<User> {
    if (!this.inited) {
      this.init();
    }
    return this.userSubject.asObservable();
  }

  public get state$(): Observable<AuthUserStateType> {
    if (!this.inited) {
      this.init();
    }
    return this.stateSubject.asObservable();
  }

  public get isAuthenticated(): boolean {
    return this._user && this._user.status !== UserStatusEnum.guest;
  }

  public get accessToken(): string {
    return this.token_srv.getAccessToken();
  }

  /* Get user object from jwt
   */
  protected _getUser(): User {
    try {
      return new User(this.token_srv.getTokenUser());
    } catch (err) {
      console.error('AuthService: _getUser error: ', err);
    }
  }

  /*
   */
  protected setTokens(access: string, refresh: string) {
    this.token_srv.setAccessToken(access);
    this.token_srv.setRefreshToken(refresh);

    const user = this._getUser();
    if (!user) {
      return;
    }
    this.refreshUser(user, (account) => {
      this.onAuthSuccess(account);
    });
  }

  /*
   */
  private init() {
    this.inited = true;
    const user: User = this._getUser();
    if (this.token_srv.getAccessToken() && user) {
      this.refreshUser(user);
    }

    // Todo. WTF?
    this.onBrowserOnly(() => {
      timer(1500, this.online_status_interval)
        .pipe(
          filter(() => !!this.isAuthenticated),
          tap(() => this.sendOnlineStatus()),
          takeUntil(this.destroyed$),
        )
        .subscribe();
    });
  }

  /*
   */
  private sendOnlineStatus() {
    if (!this.isAuthenticated || this.sendingOnlineStatus) {
      return;
    }

    this.sendingOnlineStatus = true;
    const url = `${environment.auth_url}/status-online`;
    this.http
      .put(url, {}, { observe: 'body' })
      .pipe(first())
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => (this.sendingOnlineStatus = false));
  }

  /*
   */
  private onAuthSuccess(user: User) {
    this.modal_srv.clear();
    if (!!user && this.fnAuthSuccess) {
      const fn = this.fnAuthSuccess;
      this.fnAuthSuccess = null;
      fn(user);
    }
  }

  /*
   */
  ngOnDestroy() {
    super.ngOnDestroy();
    this.fnAuthSuccess = null;
  }

  /*
   */
  public refreshUser(user: User, onSuccess?: (account: User) => void) {
    if (this.getUserById$$) {
      this.getUserById$$.unsubscribe();
    }
    this.getUserById$$ = this.getUserById(user.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (account) => {
          // TODO вынести
          this.contextService.updateMe(account);
          // this.store.dispatch(loadMeSuccess({ me: account }));
          this._user = account;
          this.userSubject.next(this._user);
          if (onSuccess) {
            onSuccess(account);
          }
        },
        (err: any) => {
          if (err instanceof NotFoundError) {
            this.logOut(true);
          }
        },
      );
  }

  /*
   */
  setUserFromResponse(access: string, refresh: string) {
    this.setTokens(access, refresh);
    this._user = this._getUser();
    this.userSubject.next(this._user);
  }

  /*
   */
  async process_auth(url: string, method: string, payload: any, captcha: string, success_cb: () => void): Promise<any> {
    const opts = {
      withCredentials: true,
      params: this.getDefReqParams(),
      ...this.getCaptchaHeaders(captcha),
    };

    return new Promise((resolve, reject) => {
      this.http[method](url, payload, opts)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          (res: IAuthTokensResponse) => {
            const { access, refresh } = res;
            if (access && refresh) {
              this.setUserFromResponse(access, refresh);
              success_cb();
              resolve(true);
              return { access, refresh };
            }
          },
          (err: any) => {
            const validationError = err?.error?.validationError;
            if (validationError && Array.isArray(validationError)) {
              reject(validationError);
              return;
            }
            if (err?.status === 403) {
              reject([{ message: err?.error?.message, path: [err?.error?.message] }]);
              return;
            }
            throwError(() => new Error(`AuthService process_auth: Error: `, err));
          },
        );
    });
  }

  /* Init password reset
   */
  public async resetPassword(data: PwdRestoreEndpoint.Body, captcha?: string) {
    const payload: PwdRestoreEndpoint.Body = data;
    const url = `${environment.auth_url}/${PwdRestoreEndpoint.path}`;
    return await this.http[PwdRestoreEndpoint.method](url, payload, {
      ...this.getCaptchaHeaders(captcha),
    }).toPromise();
  }

  /* Confirm password reset
   */
  public resetPasswordConfirm(data: PwdRestoreEndpoint.ResetConfirm) {
    const url = `${environment.auth_url}/${PwdRestoreEndpoint.action}`;
    return this.http[PwdRestoreEndpoint.confirm_method](url, data).toPromise();
  }

  /* Sign In
   */
  public signIn(data: LogInEndpoint.Body, captcha?: string): Promise<boolean> {
    const payload: LogInEndpoint.Body = data; // Todo. LogInEndpoint.parseBody(data);

    const url = `${environment.auth_url}/${LogInEndpoint.path}`;
    return this.process_auth(url, LogInEndpoint.method, payload, captcha, () => {
      this.stateSubject.next('signedIn');
      this.notificationsService.pushWelcomeGreeting();
    });
  }

  /* Sign Up
   */
  public signup(data: SignUpEndpoint.Body, captcha?: string): Promise<boolean> {
    const payload: SignUpEndpoint.Body = data; // Todo. SignUpEndpoint.parseBody(data);

    const url = `${environment.auth_url}/${SignUpEndpoint.path}`;
    return this.process_auth(url, SignUpEndpoint.method, payload, captcha, () => {
      this.stateSubject.next('signedUp');
      this.onAuthSuccess(this._user);
    });
  }

  /* SSO. New User
   */
  public ssoLogin(data: ISocialSignupRequest, captcha?: string): Promise<boolean> {
    const url = `${environment.auth_url}/social-user`;
    return this.process_auth(url, 'put', data, captcha, () => {
      this.stateSubject.next('signedIn');
      this.onAuthSuccess(this._user);
    });
  }

  /* Sign Out
   */
  public logOut(force?: boolean) {
    this._user = null;
    this.userSubject.next(this._user);
    this.stateSubject.next('signedOut');
    this.resetContext();
    this.token_srv.force_logout();
  }

  /*
   */
  protected resetContext() {
    this.contextService.updateMe(null);
    this.contextService.updateProfile(null);
  }

  /*
   */
  private getUserById(user_id: string): Observable<User> {
    const url = `${environment.account_url}/account/${user_id}`;
    return this.http.get<User>(url, { params: new HttpParams(), observe: 'response' }).pipe(
      map((response) => new User(response.body)),
      catchError(() => of(null)),
    );
  }

  /* Todo. Refactor (Unauth interceptor) (looks like we have endless reload here.).
   */
  public goAuth(backUrl?: string) {
    this.fnAuthSuccess = null;
    if (backUrl) {
      this.backUrl = backUrl;
      // logger.log('set backUrl', backUrl);
    }
  }

  /* Refactored. Todo: refactor completely
   */
  public showAuthModal(on_success?: (user: User) => void, options?: AuthModalOptions) {
    this.fnAuthSuccess = on_success || null;
    this.backUrl = null;

    if (this.isAuthenticated) {
      this.onAuthSuccess(this._user);
      return;
    }

    this.modal_srv.load_view(AuthModalComponent, () => this.modal_srv.clear(), {
      caller: options?.caller,
      reason: options?.reason,
      view: options?.view || 'signin' /* Legacy comp. */,
    });
  }

  /* TODO. REVIEW ALL CODE BELOW */
  /* ******************************************************************************** */
  /* ******************************************************************************** */
  /* ******************************************************************************** */
  /* ******************************************************************************** */
  /* ******************************************************************************** */

  public get isShowAuthModal(): boolean {
    return this.modalVisible;
  }

  /* Todo. ?
   */
  public get backUrl(): string {
    if (this._backUrl !== null) {
      return this._backUrl;
    }
    if (this.isBrowser && (localStorage || false)) {
      this._backUrl = localStorage.getItem(this.back_url_key);
    }
    return this._backUrl;
  }

  /* Todo. ?
   */
  public set backUrl(url: string) {
    this._backUrl = url;
    if (this.isBrowser && (localStorage || false)) {
      if (!url) {
        localStorage.removeItem(this.back_url_key);
      } else {
        localStorage.setItem(this.back_url_key, url);
      }
    }
  }
}
